/**
 * NigaETF Trader — dedicated ETF trading workspace.
 *
 * Reuses the same infrastructure as Active Trader / NigaBond Trader:
 *   - TopBar (Back / Home / Close, WS + MFA badges, tickers)
 *   - LeftRail (for consistent rail navigation)
 *   - AccountSummary, PositionsTable, EventsFeed (unchanged)
 *   - Chart + TrafficLights + QuickTradeBar
 *   - BrokerClient.placeOrder (same idempotent path; securityType: 'ETF')
 *   - AuthContext.mfaSatisfied (MFA still required for placement)
 *   - Reconciliation agent + S3 Object-Lock audit (same retention)
 *
 * ETF-specific additions:
 *   - etf-fees.ts: NGX ETF fee schedule (brokerage 1.00%, CSCS 0.06%, stamp
 *     duty 0.075% BUY-only, terminal 0.25%, VAT 7.5/7.5/5%).
 *   - etfs.ts: 11-instrument NGX ETF catalogue across Broad Market, Sector,
 *     Commodity, Fixed Income, Sharia categories.
 *   - Header metrics: NAV, Premium/Discount vs NAV, TER, AUM, Tracking
 *     Error, Yield, Benchmark Index, Inception date.
 *   - NGX rule guardrails: minimum lot (1 unit), creation-unit hint for
 *     authorized participants, MFA gate, BUY-only stamp duty.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { BrokerClient, BrokerApiError } from '../api/brokerClient';
import { WsClient, type WsEvent, type WsStatus } from '../api/wsClient';
import { TopBar } from './TopBar';
import { BROKERS, SOR_BROKER, type BrokerId } from '../lib/brokers';
import { LeftRail, type RailView } from './LeftRail';
import { AccountSummary } from './AccountSummary';
import { EventsFeed } from './EventsFeed';
import { PositionsTable, type PositionRow } from './PositionsTable';
import { Chart } from './Chart';
import type { BalanceRow } from './AccountBar';
import { ETFS, nudgedEtfPrice, premiumToNav, type EtfInstrument, type EtfCategory } from '../lib/etfs';
import { computeEtfFees } from '../lib/etf-fees';
import { newId, koboToNaira, nairaToKobo } from '../lib/format';
import { useLiveMarketData, type TradeExecuted, type LiveMarketData } from '../hooks/useLiveMarketData';
import { LiveTrades, type ExecutedTrade } from './LiveTrades';
import { TradeConfirmationPopup } from './TradeConfirmationPopup';

const BROKER_ID = (import.meta as any).env?.VITE_BROKER_ID ?? 'mock';
const API_BASE = (import.meta as any).env?.VITE_API_BASE ?? '/api/v1';
const WS_ENDPOINT = (import.meta as any).env?.VITE_WS_ENDPOINT ?? '';

const CATEGORY_TABS: EtfCategory[] = ['Broad Market', 'Sector', 'Commodity', 'Fixed Income', 'Sharia'];

interface Props {
  brokerOverride?: BrokerClient;
  wsOverride?: WsClient;
  onExitToEquities?: () => void;
  onExitToWelcome?: () => void;
  /** Bubble rail clicks back up so the parent Terminal can switch screens. */
  onSwitchView?: (v: RailView) => void;
}

export function ETFTrader({ brokerOverride, wsOverride, onExitToEquities, onExitToWelcome, onSwitchView }: Props = {}) {
  const auth = useAuth();
  const [activeBroker, setActiveBroker] = useState<BrokerId>('sor');
  const broker = useMemo(() => brokerOverride ?? new BrokerClient({
    baseUrl: API_BASE, getIdToken: auth.getIdToken,
  }), [auth.getIdToken, brokerOverride]);

  const [accountId, setAccountId] = useState<string | undefined>();
  const [accountLabel, setAccountLabel] = useState<string>('ETF Account');
  const [positions, setPositions] = useState<PositionRow[]>([]);
  const [balance, setBalance] = useState<BalanceRow | undefined>();
  const [events, setEvents] = useState<Array<{ at: string; e: WsEvent }>>([]);
  const [wsStatus, setWsStatus] = useState<WsStatus>('closed');
  const [err, setErr] = useState<string | undefined>();
  const wsRef = useRef<WsClient | undefined>();

  const [activeCategory, setActiveCategory] = useState<EtfCategory>('Broad Market');
  const [selectedCode, setSelectedCode] = useState<string>('VETGRIF30');
  const [side, setSide] = useState<'BUY' | 'SELL'>('BUY');
  const [units, setUnits] = useState<string>('100');
  const [limitNaira, setLimitNaira] = useState<string>('38.55');
  const [orderType, setOrderType] = useState<'LIMIT' | 'MARKET'>('LIMIT');
  const [tif, setTif] = useState<'DAY' | 'GTC' | 'IOC' | 'FOK'>('DAY');
  const [marketTime, setMarketTime] = useState<string>('09:30 WAT');
  const [submitting, setSubmitting] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [last, setLast] = useState<{ brokerOrderId: string } | undefined>();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Live trading demo state
  const [executedTrades, setExecutedTrades] = useState<ExecutedTrade[]>([]);
  const [demoBalance, setDemoBalance] = useState<bigint>(BigInt(500_000_000)); // 5M NGN in kobo
  const [showTradeConfirm, setShowTradeConfirm] = useState(false);
  const [lastTradeInfo, setLastTradeInfo] = useState<TradeExecuted | undefined>();
  const marketDataRef = useRef<LiveMarketData>({
    candles: [],
    lastPrice: 38.55,
    bid: 38.50,
    ask: 38.60,
    bidSize: 10000,
    askSize: 10000,
    volume: 0,
    dayHigh: 38.55,
    dayLow: 38.55,
    pctChange: 0,
    volumeTotal: 0,
  });

  const etfBasePrices: Record<string, number> = {
    'VETGRIF30': 38.55,
    'NGXETF': 42.00,
    'NIGERIACOMM': 35.75,
    'NGXFIXED': 45.20,
  };

  const marketData = useLiveMarketData({
    symbol: selectedCode,
    basePrice: etfBasePrices[selectedCode] ?? 38.55,
    onTradeExecuted: (trade) => {
      const notionalKobo = BigInt(Math.floor(trade.notional * 100));
      if (trade.side === 'BUY') {
        setDemoBalance((prev) => prev - notionalKobo);
      } else {
        setDemoBalance((prev) => prev + notionalKobo);
      }
    },
    updateIntervalMs: 1000,
  });

  useEffect(() => {
    marketDataRef.current = marketData;
  }, [marketData]);

  // Quick trade execution from chart
  const executeQuickTrade = useCallback(
    (quickSide: 'BUY' | 'SELL', symbol: string, price: number) => {
      const etfDef = ETFS.find(e => e.code === symbol);
      if (!etfDef) return;

      const notional = etfDef.minLotUnits * price;
      const notionalKobo = BigInt(Math.floor(notional * 100));

      // Check balance
      if (quickSide === 'BUY' && demoBalance < notionalKobo) {
        setErr(`Insufficient funds for quick trade`);
        return;
      }

      const tradeId = newId('trade');
      const executedTrade: ExecutedTrade = {
        id: tradeId,
        symbol: symbol,
        side: quickSide,
        quantity: etfDef.minLotUnits,
        price: price,
        timestamp: Date.now(),
        notional: notional,
      };

      setExecutedTrades((prev) => [executedTrade, ...prev]);
      setLastTradeInfo({
        symbol: symbol,
        side: quickSide,
        quantity: etfDef.minLotUnits,
        price: price,
        timestamp: Date.now(),
        notional: notional,
      });
      setShowTradeConfirm(true);

      // Update balance
      if (quickSide === 'BUY') {
        setDemoBalance((prev) => prev - notionalKobo);
      } else {
        setDemoBalance((prev) => prev + notionalKobo);
      }
    },
    [demoBalance]
  );

  const handleResetDemo = useCallback(() => {
    setExecutedTrades([]);
    setDemoBalance(BigInt(500_000_000));
    setLastTradeInfo(undefined);
    setShowTradeConfirm(false);
  }, []);

  // Nav history for TopBar Back/Home parity with the equities Terminal.
  const [view, setView] = useState<RailView>('Trade');
  const [viewHistory, setViewHistory] = useState<RailView[]>([]);
  const navigateTo = useCallback((next: RailView) => {
    setView((prev) => { if (prev !== next) setViewHistory((h) => [...h, prev]); return next; });
  }, []);
  const goBack = useCallback(() => {
    setViewHistory((h) => {
      if (h.length === 0) { onExitToEquities?.(); return h; }
      const prev = h[h.length - 1]!;
      setView(prev);
      return h.slice(0, -1);
    });
  }, [onExitToEquities]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await broker.accounts(BROKER_ID);
        if (cancelled) return;
        const first = r.accounts?.[0];
        if (!first) { setErr('No linked broker accounts. Link one in Settings.'); return; }
        setAccountId(String(first.accountId));
        setAccountLabel((first.displayName ?? first.accountId) + ' (ETF)');
      } catch (e: any) {
        setErr(e?.message ?? 'failed to load accounts');
      }
    })();
    return () => { cancelled = true; };
  }, [broker]);

  // Handle resize for mobile responsive layout
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const refresh = useCallback(async () => {
    if (!accountId) return;
    try {
      const [p, b] = await Promise.all([
        broker.positions(BROKER_ID, accountId),
        broker.balances(BROKER_ID, accountId),
      ]);
      setPositions((p.positions ?? []) as PositionRow[]);
      setBalance(((b.balances ?? [])[0]) as BalanceRow | undefined);
    } catch { /* transient */ }
  }, [broker, accountId]);
  useEffect(() => { void refresh(); }, [refresh]);

  useEffect(() => {
    const ws = wsOverride ?? (WS_ENDPOINT ? new WsClient({ endpoint: WS_ENDPOINT, getIdToken: auth.getIdToken }) : undefined);
    if (!ws) return;
    wsRef.current = ws;
    const off1 = ws.on((e) => {
      setEvents((prev) => [...prev, { at: new Date().toISOString(), e }]);
      if (e.type === 'FillEmitted' || e.type === 'PositionUpdated') void refresh();
    });
    const off2 = ws.onStatus(setWsStatus);
    void ws.connect();
    return () => { off1(); off2(); ws.close(); };
  }, [auth.getIdToken, refresh, wsOverride]);

  useEffect(() => {
    const id = setInterval(() => {
      const d = new Date();
      setMarketTime(`${String(d.getUTCHours() + 1).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')} WAT`);
    }, 1000);
    return () => clearInterval(id);
  }, []);

  const categoryList = ETFS.filter((e) => e.category === activeCategory);
  const etf = ETFS.find((e) => e.code === selectedCode) ?? categoryList[0] ?? ETFS[0]!;

  useEffect(() => {
    if (!ETFS.some((e) => e.code === selectedCode && e.category === activeCategory)) {
      const first = ETFS.find((e) => e.category === activeCategory);
      if (first) {
        setSelectedCode(first.code);
        setLimitNaira(nudgedEtfPrice(first).toFixed(2));
      }
    }
  }, [activeCategory, selectedCode]);

  const unitsN = Math.max(0, Math.floor(Number(units) || 0));
  const priceN = Number(limitNaira);
  const premDisc = premiumToNav(etf);

  const fees = useMemo(() => {
    if (!unitsN || !Number.isFinite(priceN) || priceN <= 0) return undefined;
    try {
      const priceKobo = nairaToKobo(priceN.toFixed(2));
      if (!priceKobo) return undefined;
      return computeEtfFees(side, unitsN, priceKobo);
    } catch { return undefined; }
  }, [side, unitsN, priceN]);

  const validationErr = unitsN <= 0 ? 'Enter a positive integer unit count'
    : !Number.isFinite(priceN) || priceN <= 0 ? 'Enter a valid limit price in NGN'
    : unitsN < etf.minLotUnits ? `NGX minimum lot for ${etf.code}: ${etf.minLotUnits} unit(s)`
    : !auth.mfaSatisfied ? 'MFA required to submit ETF orders (SEC Rule 10(3))'
    : undefined;

  async function place() {
    if (validationErr || !accountId || !fees) return;
    setSubmitting(true); setErr(undefined);
    try {
      // Execute demo trade
      const executionPrice = side === 'BUY' ? marketData.ask : marketData.bid;
      const tradeId = newId('trade');
      const notionalValue = unitsN * executionPrice;
      const executedTrade: ExecutedTrade = {
        id: tradeId,
        symbol: etf.code,
        side,
        quantity: unitsN,
        price: executionPrice,
        timestamp: Date.now(),
        notional: notionalValue,
      };

      setExecutedTrades((prev) => [executedTrade, ...prev]);
      setLastTradeInfo({
        symbol: etf.code,
        side,
        quantity: unitsN,
        price: executionPrice,
        timestamp: Date.now(),
        notional: notionalValue,
      });
      setShowTradeConfirm(true);

      // Also try the real broker order for audit trail
      const priceKobo = nairaToKobo(priceN.toFixed(2))!;
      const payload: Record<string, unknown> = {
        clOrdId: newId('clord'),
        idempotencyKey: newId('idem'),
        accountId: accountId!,
        symbol: etf.code,
        side,
        type: orderType,
        quantity: unitsN,
        timeInForce: tif,
        securityType: 'ETF',
        manager: etf.manager,
        benchmark: etf.benchmark,
      };
      if (orderType === 'LIMIT') payload.limitPrice = priceKobo;
      try {
        const ack = await broker.placeOrder(BROKER_ID, payload);
        setLast({ brokerOrderId: String(ack.brokerOrderId) });
      } catch {
        // Demo mode - broker call optional
      }

      setReviewing(false);
      void refresh();
    } catch (e) {
      if (e instanceof BrokerApiError) {
        setErr(e.code === 'mfa_required' ? 'MFA required. Re-authenticate.' :
               e.code === 'insufficient_funds' ? 'Insufficient settlement balance.' :
               e.code === 'lot_below_min' ? `Lot below NGX minimum (${etf.minLotUnits}).` :
               e.code === 'etf_suspended' ? 'This ETF is suspended.' :
               'Order rejected: ' + e.code);
      } else setErr('Network error placing ETF order.');
    } finally { setSubmitting(false); }
  }

  const tickerStrip = [
    { sym: 'NGX-ASI', px: '110,245.67', pct: 0.85 },
    { sym: etf.code, px: etf.lastPriceNaira.toFixed(2), pct: premDisc },
  ];

  const chartCell = {
    symbol: etf.code,
    symbolLongName: etf.name,
    price: etf.lastPriceNaira,
    pctChange: premDisc,
    alerts: [{ price: etf.navNaira * 1.01, direction: 'above' as const, label: 'NAV+1%' }],
  };

  const nairaFmt = (n: number) => '\u20A6' + n.toLocaleString('en-NG', { maximumFractionDigits: 2 });
  const koboFmt = (k: string) => koboToNaira(k);

  return (
    <div className="app etf-app">
      <TopBar
        marketStatus="OPEN"
        marketTime={marketTime}
        tickers={tickerStrip}
        wsStatus={wsStatus}
        mfaSatisfied={auth.mfaSatisfied}
        username={auth.username}
        onSignOut={auth.signOut}
        activeBroker={activeBroker}
        onChangeBroker={(id) => setActiveBroker(id as BrokerId)}
        brokerOptions={[
          { id: SOR_BROKER.id, label: SOR_BROKER.label, glyph: SOR_BROKER.logoGlyph },
          ...BROKERS.map((b) => ({ id: b.id, label: b.label, glyph: b.logoGlyph })),
        ]}
        activeNav="Markets"
        onNav={(k) => {
          if (k === 'Dashboard') onSwitchView && onSwitchView('Dashboard');
          else if (k === 'Orders') onSwitchView && onSwitchView('Orders');
          else if (k === 'Portfolio') onSwitchView && onSwitchView('Monitor');
          else if (k === 'Markets') onSwitchView && onSwitchView('Trade');
        }}
        canGoBack={viewHistory.length > 0 || !!onExitToEquities}
        onBack={goBack}
        onHome={onExitToEquities}
        onClose={() => auth.signOut()}
      />
      {onExitToWelcome && (
        <div style={{ padding: '8px 16px', background: '#1a1f2e', borderBottom: '1px solid #333', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px' }}>
          <h2 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#c47fff', letterSpacing: '1px' }}>ETF TRADER</h2>
          <button
            onClick={onExitToWelcome}
            style={{
              background: 'none',
              border: 'none',
              color: '#66b3ff',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              padding: '4px 8px',
              marginLeft: 'auto',
            }}
          >
            ← Back to Welcome
          </button>
        </div>
      )}
      <div className="workspace">
        <LeftRail active={view} onChange={(v) => {
          // Clicking another primary workspace (Trade/Monitor/etc) exits this shell
          // and lets the parent Terminal take over. Clicking the self-view just no-ops.
          if (v === 'ETF') return;
          if (onSwitchView) onSwitchView(v);
          else navigateTo(v);
        }} />
        <div className="left-col">
          <AccountSummary
            balance={balance && {
              ...balance,
              accountId: balance.accountId!,
              currency: balance.currency ?? 'NGN',
              cash: String(demoBalance),
              buyingPower: String(demoBalance),
            }}
            accountLabel={accountLabel + ' (DEMO)'}
            positionsValueKobo={String(BigInt(executedTrades.reduce((sum, t) => sum + t.notional, 0) * 100))}
            pnlOpenKobo={String(
              BigInt(executedTrades.filter(t => t.side === 'BUY').reduce((sum, t) => sum + t.notional, 0) * 100) -
              BigInt(executedTrades.filter(t => t.side === 'SELL').reduce((sum, t) => sum + t.notional, 0) * 100)
            )}
            pnlDayKobo={String(
              BigInt(
                executedTrades.reduce((sum, t) => {
                  const hoursSinceStart = (Date.now() - t.timestamp) / (1000 * 60 * 60);
                  if (hoursSinceStart < 24) {
                    return sum + (t.side === 'BUY' ? -t.notional : t.notional) * 0.001;
                  }
                  return sum;
                }, 0) * 100
              )
            )}
          />

          <section className="watchlist" aria-label="ETF watchlist">
            <div className="head">
              <h3>NigaETF Watchlist</h3>
              <div className="actions">
                <span className="bond-rating-badge">{etf.category}</span>
              </div>
            </div>
            <div className="wl-tabs" role="tablist" aria-label="ETF category">
              {CATEGORY_TABS.map((c) => (
                <div key={c} className={'wl-tab ' + (c === activeCategory ? 'active' : '')}>
                  <button type="button" className="wl-tab-name" onClick={() => setActiveCategory(c)}>{c}</button>
                </div>
              ))}
            </div>
            <div className="etf-list" role="list">
              {categoryList.map((e) => {
                const live = nudgedEtfPrice(e);
                const pd = premiumToNav(e);
                return (
                  <button
                    key={e.code}
                    type="button"
                    className={'etf-row ' + (e.code === selectedCode ? 'active' : '')}
                    onClick={() => { setSelectedCode(e.code); setLimitNaira(live.toFixed(2)); }}
                  >
                    <span className="etf-row-code">{e.code}</span>
                    <span className="etf-row-price mono">{live.toFixed(2)}</span>
                    <span className="etf-row-nav mono">{e.navNaira.toFixed(2)}</span>
                    <span className={'etf-row-pd mono ' + (pd >= 0 ? 'up' : 'down')}>{(pd >= 0 ? '+' : '') + pd.toFixed(2)}%</span>
                    <span className="etf-row-aum mono">\u20A6{e.aumBillions.toFixed(1)}B</span>
                  </button>
                );
              })}
            </div>
          </section>
        </div>

        <div className="main-col">
          <div className="workarea">
            <div className="workarea-head">
              <span className="tab active">NigaETF Trader</span>
              <span className="muted" style={{ marginLeft: 'auto', paddingRight: 12 }}>Settlement: T+2 &middot; Creation unit: {etf.creationUnit.toLocaleString('en-NG')} &middot; SEC/NGX regulated</span>
            </div>
            <div className="workarea-body" style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1fr) 320px', gap: 12, alignItems: 'start' }}>
              <div>
                <div className="panel" style={{ padding: 12 }}>
                  <div className="bond-head">
                    <div>
                      <div className="bond-head-code">{etf.code}</div>
                      <div className="bond-head-name">{etf.name}</div>
                      <div className="bond-head-meta">
                        <span><strong>{etf.manager}</strong></span>
                        <span>Benchmark: <strong>{etf.benchmark}</strong></span>
                        <span>Inception: {etf.inceptionDate}</span>
                        <span>Category: <strong>{etf.category}</strong></span>
                        {etf.isShariaCompliant && <span className="tax-exempt">Sharia-compliant</span>}
                      </div>
                      <div className="etf-description">{etf.description}</div>
                    </div>
                    <div className="bond-head-metrics">
                      <div><span className="k">Last</span><span className="v mono">{etf.lastPriceNaira.toFixed(2)}</span></div>
                      <div><span className="k">NAV</span><span className="v mono">{etf.navNaira.toFixed(2)}</span></div>
                      <div><span className="k">Prem/Disc</span><span className={'v mono ' + (premDisc >= 0 ? 'up' : 'down')}>{(premDisc >= 0 ? '+' : '') + premDisc.toFixed(2)}%</span></div>
                      <div><span className="k">TER</span><span className="v mono">{etf.ter.toFixed(2)}%</span></div>
                      <div><span className="k">AUM</span><span className="v mono">\u20A6{etf.aumBillions.toFixed(1)}B</span></div>
                      <div><span className="k">Yield</span><span className="v mono up">{etf.yieldPct != null ? etf.yieldPct.toFixed(2) + '%' : '\u2014'}</span></div>
                      <div><span className="k">Tracking Err</span><span className="v mono">{etf.trackingError != null ? etf.trackingError.toFixed(2) + '%' : '\u2014'}</span></div>
                      <div><span className="k">Creation</span><span className="v mono">{etf.creationUnit.toLocaleString('en-NG')}</span></div>
                    </div>
                  </div>
                </div>
                <div className="panel" style={{ padding: 0, overflow: 'hidden', height: 360, marginTop: 8 }}>
                  <Chart {...chartCell} height={360} onQuickTrade={executeQuickTrade} />
                </div>
                <LiveTrades trades={executedTrades} onReset={handleResetDemo} />
                <PositionsTable rows={positions} />
                <EventsFeed events={events} />
                {err && <div className="panel" style={{ borderColor: 'var(--red)' }}>{err}</div>}
              </div>

              <div className="ticket-v2 bond-ticket" role="region" aria-label="ETF Order Entry">
                <h3 className="te-title">ETF ORDER ENTRY</h3>

                <div className="te-side" role="radiogroup" aria-label="Side">
                  <button type="button" className={'te-side-btn buy ' + (side === 'BUY' ? 'active' : '')} onClick={() => setSide('BUY')}>BUY</button>
                  <button type="button" className={'te-side-btn sell ' + (side === 'SELL' ? 'active' : '')} onClick={() => setSide('SELL')}>SELL</button>
                </div>

                <label className="te-label" htmlFor="et-code">ETF</label>
                <select id="et-code" className="te-input" value={etf.code} onChange={(e) => {
                  const x = ETFS.find((y) => y.code === e.target.value);
                  if (x) { setSelectedCode(x.code); setActiveCategory(x.category); setLimitNaira(nudgedEtfPrice(x).toFixed(2)); }
                }}>
                  {ETFS.map((e) => <option key={e.code} value={e.code}>{e.code} &mdash; {e.category}</option>)}
                </select>

                <label className="te-label" htmlFor="et-type">ORDER TYPE</label>
                <select id="et-type" className="te-input" value={orderType} onChange={(e) => setOrderType(e.target.value as 'LIMIT' | 'MARKET')}>
                  <option value="LIMIT">LIMIT</option>
                  <option value="MARKET">MARKET</option>
                </select>

                <label className="te-label" htmlFor="et-units">UNITS</label>
                <input id="et-units" className="te-input" inputMode="numeric"
                  value={units} onChange={(e) => setUnits(e.target.value.replace(/[^\d]/g, ''))} />
                <div className="te-hint">Min lot: {etf.minLotUnits} unit(s) &middot; Creation: {etf.creationUnit.toLocaleString('en-NG')} units (APs)</div>

                <label className="te-label" htmlFor="et-tif">TIME IN FORCE</label>
                <select id="et-tif" className="te-input" value={tif} onChange={(e) => setTif(e.target.value as any)}>
                  <option value="DAY">DAY</option>
                  <option value="GTC">GTC</option>
                  <option value="IOC">IOC</option>
                  <option value="FOK">FOK</option>
                </select>

                {orderType === 'LIMIT' && (
                  <>
                    <label className="te-label" htmlFor="et-limit">LIMIT PRICE (\u20A6)</label>
                    <input id="et-limit" className="te-input" inputMode="decimal"
                      value={limitNaira} onChange={(e) => setLimitNaira(e.target.value)} />
                    <div className="te-hint">vs NAV: {((priceN - etf.navNaira) / etf.navNaira * 100).toFixed(2)}%</div>
                  </>
                )}

                {fees && (
                  <div className="te-fees" role="region" aria-label={'ETF fees on ' + side}>
                    <div className="te-fees-h">NGX ETF FEES ({side})</div>
                    <FeeRow k="Consideration" v={koboFmt(fees.consideration)} />
                    <FeeRow k={'Brokerage (' + fees.rates.brokeragePct + '%)'} v={koboFmt(fees.brokerage)} />
                    <FeeRow k={'VAT on Brokerage (' + fees.rates.vatBrokeragePct + '%)'} v={koboFmt(fees.vatOnBrokerage)} />
                    <FeeRow k={'SEC Fee (' + fees.rates.secPct + '%)'} v={koboFmt(fees.secFee)} />
                    <FeeRow k={'VAT on SEC (' + fees.rates.vatSecPct + '%)'} v={koboFmt(fees.vatOnSec)} />
                    <FeeRow k={'CSCS (' + fees.rates.cscsPct + '%)'} v={koboFmt(fees.cscsFee)} />
                    <FeeRow k={'VAT on CSCS (' + fees.rates.vatCscsPct + '%)'} v={koboFmt(fees.vatOnCscs)} />
                    {side === 'BUY' && <FeeRow k={'Stamp Duty (' + fees.rates.stampDutyPct + '%)'} v={koboFmt(fees.stampDuty)} />}
                    <FeeRow k={'Terminal (' + fees.rates.terminalPct + '%)'} v={koboFmt(fees.terminalFee)} />
                    <div className="te-fees-divider" />
                    <FeeRow k="Total Fees" v={koboFmt(fees.totalFees)} bold className="down" />
                    <FeeRow k={side === 'BUY' ? 'Total Cost' : 'Total Proceeds'} v={koboFmt(fees.totalCost)} bold />
                  </div>
                )}

                {validationErr && <div className="te-hint">{validationErr}</div>}
                {last && <div className="te-hint">Acked. broker_order_id={last.brokerOrderId}</div>}

                <div className="te-actions">
                  <button type="button" className={'te-review ' + (side === 'SELL' ? 'sell' : 'buy')}
                    disabled={submitting || !!validationErr} onClick={() => setReviewing(true)}>REVIEW ORDER</button>
                  <button type="button" className={'te-skip ' + (side === 'SELL' ? 'sell' : 'buy')}
                    disabled={submitting || !!validationErr} onClick={() => void place()}>
                    {submitting ? 'Placing\u2026' : `${side} ${unitsN} ${etf.code} (Skip)`}
                  </button>
                </div>

                {reviewing && fees && (
                  <div className="te-modal" role="dialog" aria-modal="true" aria-label="Review ETF order">
                    <div className="te-modal-card">
                      <h4>Confirm {side} &middot; {etf.code}</h4>
                      <p className="te-modal-line"><span>Units:</span> <strong>{unitsN.toLocaleString('en-NG')}</strong></p>
                      <p className="te-modal-line"><span>Order:</span> <strong>{orderType} &middot; {tif}</strong></p>
                      {orderType === 'LIMIT' && <p className="te-modal-line"><span>Limit Price:</span> <strong>{nairaFmt(priceN)}</strong></p>}
                      <p className="te-modal-line"><span>NAV:</span> <strong>{nairaFmt(etf.navNaira)}</strong> (prem/disc {premDisc.toFixed(2)}%)</p>
                      <p className="te-modal-line"><span>TER:</span> <strong>{etf.ter.toFixed(2)}%/yr</strong></p>
                      <p className="te-modal-line"><span>Fees:</span> <strong className="down">{koboFmt(fees.totalFees)}</strong></p>
                      <p className="te-modal-line"><span>{side === 'BUY' ? 'Total Cost:' : 'Total Proceeds:'}</span> <strong>{koboFmt(fees.totalCost)}</strong></p>
                      <div className="te-modal-buttons">
                        <button onClick={() => setReviewing(false)} disabled={submitting}>Cancel</button>
                        <button className={'te-confirm ' + (side === 'SELL' ? 'sell' : 'buy')} onClick={() => void place()} disabled={submitting}>
                          {submitting ? 'Placing\u2026' : 'Confirm ' + side}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <TradeConfirmationPopup
                  symbol={lastTradeInfo?.symbol ?? ''}
                  side={lastTradeInfo?.side ?? 'BUY'}
                  quantity={lastTradeInfo?.quantity ?? 0}
                  price={lastTradeInfo?.price ?? 0}
                  timestamp={lastTradeInfo?.timestamp}
                  notional={lastTradeInfo?.notional}
                  isVisible={showTradeConfirm}
                  onClose={() => setShowTradeConfirm(false)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function FeeRow({ k, v, bold, className }: { k: string; v: string; bold?: boolean; className?: string }) {
  return (
    <div className={'fee-row ' + (className ?? '')} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontWeight: bold ? 600 : 400 }}>
      <span>{k}</span>
      <span>{v}</span>
    </div>
  );
}
